import React, {ReactElement, useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import 'rc-slider/assets/index.css';
import {IValidationErrorObject} from '../../../../incase-domain/common/validationErrorResponse';
import Button from '../../../../incase-domain/components/button/Button';
import ChecklistTile from '../../../../incase-domain/components/checkbox/ChecklistTile';
import DebouncedSlider from '../../../../incase-domain/components/slider/DebouncerSliderElement';
import {IElementImageMarkup} from '../../../../incase-domain/elements/question/imageMarkup';
import {generateErrorMessages, getErrorClasses} from '../../../../utils/validation';
import useImgFileReader from '../../../fileLoader/util/useImgFileReader';
import {TABS} from '../../form/part/tabs';
import BorderRadiusChange from '../common/borderRadiusChange';
import ElementShadow from '../common/elementShadow';
import ImageMarkupModal from '../ImageMarkupModal';
import HighlightedInputMedia from '../../../fileLoader/input.media';
import AdvancedOptions from './components/advancedOptions/AdvancedOptions';
import QuestionOptions, {QUESTION_OPTIONS} from './components/QuestionOptions';
import QuestionText from './components/QustionText';
import {useHighlightedElement, useSetHighlightedElement} from '../../../../utils/hooks/slideUpdates/useSetSlideElement';
import AnimationForm from '../common/animationForm';

interface HighlightedImageMarkupElementComponentProps {
    errors: IValidationErrorObject | null;
    activeTab: string;
}

function HighlightedImageMarkupElementComponent(props: HighlightedImageMarkupElementComponentProps): ReactElement {
    const {errors, activeTab} = props;
    const {t} = useTranslation();
    const updateHighlightedElement = useSetHighlightedElement();
    const highlightedElement = useHighlightedElement() as IElementImageMarkup;

    const [markImageModalOpen, setMarkImageModalOpen] = useState<boolean>(false);

    useEffect(() => {
        const {imageSrc, markedAreas} = highlightedElement;

        if (imageSrc && !markedAreas.length) {
            setMarkImageModalOpen(true);
        }
    }, [highlightedElement, highlightedElement.imageSrc, highlightedElement.markedAreas]);

    const onFileLoad = useImgFileReader((images) => {
        const {imageSrc, widthHeightRatio} = images[0];

        updateHighlightedElement.multiple(
            ['imageSrc', 'fitContent', 'widthHeightRatio', 'markedAreas'],
            [imageSrc, true, widthHeightRatio, []]
        );
    });

    useEffect(() => {
        if (!highlightedElement.imageSrc) setMarkImageModalOpen(true);
    }, [highlightedElement.imageSrc]);

    if (activeTab === TABS.STYLE.id) {
        return (
            <>
                <AdvancedOptions highlightedElement={highlightedElement} highlightedElementChangeHandler={updateHighlightedElement} />

                <div className="l-group--form">
                    <div className="l-group__item">
                        <BorderRadiusChange
                            borderRadius={highlightedElement.borderRadius}
                            highlightedElementChangeHandler={(value) => updateHighlightedElement.single('borderRadius', value)}
                        />
                    </div>
                </div>

                <ElementShadow highlightedElement={highlightedElement} highlightedElementChangeHandler={updateHighlightedElement} />
            </>
        );
    }

    if (activeTab === TABS.ADVANCED.id) {
        return (
            <>
                <QuestionOptions
                    highlightedElement={highlightedElement}
                    highlightedElementChangeHandler={updateHighlightedElement}
                    questionOptions={[QUESTION_OPTIONS.BLOCKING_QUESTION, QUESTION_OPTIONS.SHOW_IN_REPORT, QUESTION_OPTIONS.RETRY_QUESTION]}
                />
                <AnimationForm
                    appearAfter={highlightedElement.appearAfter || 0}
                    elementAnimations={highlightedElement.elementAnimations}
                    highlightedElementChangeHandler={updateHighlightedElement}
                />
            </>
        );
    }

    return (
        <>
            <QuestionText
                highlightedElement={highlightedElement}
                errors={errors}
                highlightedElementChangeHandler={updateHighlightedElement}
            />
            <div className="l-group--form">
                <div className={`l-group__item ${getErrorClasses(errors, 'imageSrc')}`}>
                    <HighlightedInputMedia type="image" hasExistingFile={!!highlightedElement.imageSrc} onLoad={onFileLoad} />
                    {errors && generateErrorMessages(errors, 'imageSrc')}
                </div>
                <div className={`l-group__item ${getErrorClasses(errors, 'markedAreas')}`}>
                    <Button variant="soft" color="secondary" size="2xs" fullWidth onClick={() => setMarkImageModalOpen(true)}>
                        {t('designSidepanel.markImageArea')}
                    </Button>
                    {generateErrorMessages(errors, 'markedAreas')}
                </div>
            </div>

            <div className="l-group--form">
                <div className="l-group__item">
                    <ChecklistTile
                        value={highlightedElement.intersectionAnswerType}
                        label={t('designSidepanel.intersectionAnswerType')}
                        help={t('designSidepanel.intersectionAnswerTypeExpl')}
                        onChange={(value) => updateHighlightedElement.single('intersectionAnswerType', value)}
                    />
                </div>

                {highlightedElement.intersectionAnswerType && (
                    <div className="l-group__item u-flex u-h-between" style={{padding: '0 10px 10px 10px'}}>
                        <DebouncedSlider
                            min={0.2}
                            max={0.8}
                            step={0.05}
                            value={highlightedElement.correctAnswerMinIntersection}
                            marks={{
                                0.2: '20%',
                                0.4: '40%',
                                0.6: '60%',
                                0.8: '80%',
                            }}
                            onChange={(value) => updateHighlightedElement.single('correctAnswerMinIntersection', value)}
                        />
                    </div>
                )}
            </div>

            <ImageMarkupModal
                element={highlightedElement}
                isOpen={markImageModalOpen && !!highlightedElement.imageSrc}
                onCancel={() => setMarkImageModalOpen(false)}
            />
        </>
    );
}

export default HighlightedImageMarkupElementComponent;
